import { useCallback, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Bell } from 'lucide-react';
import { OrganizationsHeader } from './components/OrganizationsHeader/OrganizationsHeader';
import { YourEventsSection } from './components/YourEventsSection/YourEventsSection';
import { CurrentActivitiesSection } from './components/CurrentActivitiesSection/CurrentActivitiesSection';
import { NewEventsFromOrgsSection } from './components/NewEventsFromOrgsSection/NewEventsFromOrgsSection';
import { MarketplaceSection } from './components/MarketplaceSection/MarketplaceSection';
import { VolunteerJoinEventModal } from './components/VolunteerJoinEventModal/VolunteerJoinEventModal';
import { useMarketplaceOrganizations } from '@/hooks/useMarketplaceOrganizations';
import type { Organization } from '@/types/events';
import { FilterModal } from './components/FilterModal/FilterModal';
import { EventModal } from '@/components/common/EventModal';
import { LocationPicker, NavBar } from '@/components/ui';
import { ConfirmModal } from '@/components/ui/ConfirmModal/ConfirmModal';
import { Toast } from '@/components/ui/Toast/Toast';
import { OrgContextSwitcher } from '@/components/ui/OrgContextSwitcher/OrgContextSwitcher';
import EventPreviewModal, { type ProfileEvent } from '@/components/common/EventPreviewModal';
import { PullToRefresh } from '@/components/common/PullToRefresh';
import { useOrganizationsPage } from './useOrganizationsPage';
import { useUser } from '@/constants/UserContext';
import { useUserEvents } from '@/hooks/useUserEvents';
import { useLeaveEvent } from '@/hooks/useLeaveEvent';
import { useToast } from '@/hooks/useToast';

export function OrganizationsPage() {
  const { activeAdminOrgId } = useUser();

  // For volunteer acting as org admin, load that org's events separately
  const {
    events: adminOrgEvents,
    loading: adminOrgEventsLoading,
    refetch: refetchAdminOrgEvents,
  } = useUserEvents(activeAdminOrgId);

  const {
    userId,
    isOrganization,
    subscriptionActive,
    startDate,
    endDate,
    selectedLocations,
    tempSelectedLocations,
    showFilterModal,
    setShowFilterModal,
    showLocationPicker,
    setShowLocationPicker,
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    showEventPreview,
    setShowEventPreview,
    editingEvent,
    setEditingEvent,
    selectedEvent,
    setSelectedEvent,
    isLocationPickerLoading,
    publicEvents,
    userEvents,
    userEventsLoading,
    activities,
    newOrgEvents,
    newOrgEventsSectionHeading,
    handleEventPress,
    handleShiftsPress,
    previewSection,
    setPreviewSection,
    handleEditEvent,
    handleManageEvent,
    handleCreateClick,
    handleCreateSuccess,
    handleEditSuccess,
    handleFilterApply,
    handleLocationPickerOpen,
    handleLocationSelect,
    handleNavigateToVolunteers,
    filteredPublicEvents,
    publicLoading,
    refetchPublicEvents,
    refetchUserEvents,
    refetchActivities,
    refetchMembershipOrgIds,
  } = useOrganizationsPage();

  const navigate = useNavigate();
  const [joinEventModal, setJoinEventModal] = useState<Organization | null>(null);

  // When a volunteer is acting as an org admin, subscription gating should be based on
  // the organization (not the volunteer profile). Until we fetch org subscription explicitly,
  // don't block edit/manage/create in org-admin context.
  const effectiveSubscriptionActive = isOrganization
    ? subscriptionActive
    : activeAdminOrgId
      ? true
      : subscriptionActive;

  const {
    organizations: marketplaceOrganizations,
    loading: marketplaceOrgsLoading,
    joinOrganization,
    refetch: refetchMarketplaceOrgs,
  } = useMarketplaceOrganizations(!isOrganization ? userId : null);

  const { toast, showToast, hideToast } = useToast();

  const handleLeft = useCallback(() => {
    // The card comes from useCurrentActivities; the marketplace CTA flips back to "Join".
    void refetchActivities();
    void refetchPublicEvents();
  }, [refetchActivities, refetchPublicEvents]);

  const { pendingEvent, leaving, requestLeave, confirmLeave, cancelLeave } = useLeaveEvent(
    showToast,
    handleLeft
  );

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      refetchPublicEvents(),
      refetchMarketplaceOrgs(),
      refetchAdminOrgEvents(),
      refetchUserEvents(),
      refetchActivities(),
      refetchMembershipOrgIds(),
    ]);
  }, [
    refetchPublicEvents,
    refetchMarketplaceOrgs,
    refetchAdminOrgEvents,
    refetchUserEvents,
    refetchActivities,
    refetchMembershipOrgIds,
  ]);

  return (
    <Box minH="100vh" bg="white" pl={{ base: 0, lg: 'var(--sidebar-width)' }} transition="all 0.3s">
      <PullToRefresh onRefresh={handleRefresh}>
      <Box
        maxW={{ base: '100%', md: '920px', xl: '1040px' }}
        mx="auto"
        py="5"
        px={{ base: '5', md: '6' }}
        pb={{ base: 'var(--mobile-bottom-safe)', lg: '6' }}
      >
        <OrganizationsHeader />

        {/* Org context switcher — shown to volunteers with admin org access */}
        <OrgContextSwitcher />

        {/* Volunteer quick actions */}
        {!isOrganization && !activeAdminOrgId && (
          <Flex gap="3" mb="6" mt="1">
            <button
              type="button"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '16px 20px',
                background: '#007AFF',
                color: 'white',
                borderRadius: '20px',
                fontWeight: 600,
                fontSize: '16px',
                cursor: 'pointer',
                border: '1px solid #007AFF',
                boxShadow: '0 4px 14px rgba(0,122,255,0.25)',
                transition: 'all 0.2s',
                fontFamily: 'inherit',
              }}
              onClick={() => navigate('/volunteer-tasks')}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = '#0063CC';
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#0063CC';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = '#007AFF';
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#007AFF';
              }}
            >
              <BarChart3 size={20} />
              Tasks
            </button>
            <button
              type="button"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '16px 20px',
                background: 'white',
                color: '#1F2932',
                border: '2px solid #1F2932',
                borderRadius: '20px',
                fontWeight: 600,
                fontSize: '16px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(31,41,50,0.10)',
                transition: 'all 0.2s',
                fontFamily: 'inherit',
              }}
              onClick={() => navigate('/notifications')}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = '#F2F2F7';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'white';
              }}
            >
              <Bell size={20} />
              Notifications
            </button>
          </Flex>
        )}

        {/* Native org user: their own events */}
        {isOrganization && (
          <YourEventsSection
            events={userEvents}
            loading={userEventsLoading}
            subscriptionActive={effectiveSubscriptionActive}
            onEventPress={handleEventPress}
            onEditEvent={(event) => handleEditEvent(event)}
            onManageEvent={handleManageEvent ? (event) => handleManageEvent(event) : undefined}
            onCreateClick={handleCreateClick}
          />
        )}

        {/* Volunteer acting as org admin: show that org's events */}
        {!isOrganization && activeAdminOrgId && (
          <YourEventsSection
            events={adminOrgEvents}
            loading={adminOrgEventsLoading}
            subscriptionActive={effectiveSubscriptionActive}
            onEventPress={handleEventPress}
            onEditEvent={(event) => handleEditEvent(event)}
            onManageEvent={handleManageEvent ? (event) => handleManageEvent(event) : undefined}
            onCreateClick={handleCreateClick}
          />
        )}

        {!isOrganization && !activeAdminOrgId && (
          <CurrentActivitiesSection
            activities={activities}
            onEventPress={handleEventPress}
            onLeave={(activity) => requestLeave({ id: activity.id, name: activity.name })}
            onShifts={handleShiftsPress}
          />
        )}

        {!isOrganization && !activeAdminOrgId && (
          <NewEventsFromOrgsSection
            events={newOrgEvents}
            sectionHeading={newOrgEventsSectionHeading}
            onEventPress={handleEventPress}
          />
        )}

        {!isOrganization && !activeAdminOrgId && (
          <MarketplaceSection
            events={filteredPublicEvents}
            organizations={marketplaceOrganizations}
            loadingEvents={publicLoading}
            loadingOrganizations={marketplaceOrgsLoading}
            volunteerUserId={userId}
            onEventPress={handleEventPress}
            onJoinOrganization={joinOrganization}
          />
        )}

        {isOrganization && null}
      </Box>
      </PullToRefresh>

      <EventPreviewModal
        key={selectedEvent?.id ?? 'no-event'}
        visible={showEventPreview}
        onClose={() => {
          setShowEventPreview(false);
          setSelectedEvent(null);
          setPreviewSection(null);
        }}
        event={selectedEvent as ProfileEvent | null}
        scrollToShifts={previewSection === 'shifts'}
        isOrganization={isOrganization}
        hideOrganizationInfo={false}
        onJoinEvent={
          isOrganization
            ? undefined
            : (ev) => {
                setShowEventPreview(false);
                setSelectedEvent(null);
                setJoinEventModal(ev as Organization);
              }
        }
        onNavigateToVolunteers={(eventId) => handleNavigateToVolunteers(eventId)}
        onOpenTasks={(eventId) =>
          navigate(
            `/volunteers?orgTab=events&eventId=${encodeURIComponent(eventId)}&open=tasks`,
          )
        }
        onOpenVolunteerTasks={
          isOrganization
            ? undefined
            : (eventId) =>
                navigate(`/volunteer-tasks?event=${encodeURIComponent(eventId)}`)
        }
        onLeftEvent={handleLeft}
      />

      <ConfirmModal
        isOpen={pendingEvent != null}
        title="Leave Event"
        message={`Are you sure you want to leave${pendingEvent?.name ? ` "${pendingEvent.name}"` : ' this event'}? You'll lose access to its chats and tasks, and your spot will be freed up.`}
        confirmText={leaving ? 'Please wait…' : 'Leave'}
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => void confirmLeave()}
        onCancel={cancelLeave}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}

      <VolunteerJoinEventModal
        isOpen={joinEventModal != null}
        event={joinEventModal}
        onClose={() => setJoinEventModal(null)}
        onSuccess={() => {
          refetchPublicEvents();
          refetchMarketplaceOrgs();
        }}
      />

      <FilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApply={handleFilterApply}
        initialFilters={{ startDate, endDate, selectedLocations }}
        onLocationPickerOpen={handleLocationPickerOpen}
        isLocationPickerLoading={isLocationPickerLoading}
        tempSelectedLocations={tempSelectedLocations}
      />

      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onSelect={handleLocationSelect}
        selectedLocations={tempSelectedLocations}
        events={publicEvents}
      />

      {/* Event modals for native org users */}
      {isOrganization && userId && (
        <>
          <EventModal
            mode="create"
            visible={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleCreateSuccess}
            userId={userId}
            subscriptionActive={effectiveSubscriptionActive}
          />
          <EventModal
            mode="edit"
            visible={showEditModal}
            event={editingEvent}
            onClose={() => {
              setShowEditModal(false);
              setEditingEvent(null);
            }}
            onSuccess={handleEditSuccess}
            userId={userId}
            subscriptionActive={effectiveSubscriptionActive}
          />
        </>
      )}

      {/* Event modals for volunteer acting as org admin */}
      {!isOrganization && activeAdminOrgId && (
        <>
          <EventModal
            mode="create"
            visible={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => { handleCreateSuccess(); refetchAdminOrgEvents(); }}
            userId={activeAdminOrgId}
            subscriptionActive={effectiveSubscriptionActive}
          />
          <EventModal
            mode="edit"
            visible={showEditModal}
            event={editingEvent}
            onClose={() => {
              setShowEditModal(false);
              setEditingEvent(null);
            }}
            onSuccess={() => { handleEditSuccess(); refetchAdminOrgEvents(); }}
            userId={activeAdminOrgId}
            subscriptionActive={effectiveSubscriptionActive}
          />
        </>
      )}

      <NavBar />
    </Box>
  );
}
